import { useState } from "react";
import { X, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { createProduct } from "../../../redux/slices/productSlice";
import { Input, Textarea } from "../Common/CardUIHelpers";

interface AddProductOverlayProps {
  onClose: () => void;
}

export const AddProductOverlay = ({ onClose }: AddProductOverlayProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    image: "",
    tags: "",
    inStock: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};

    if (!form.name) newErrors.name = "Product name is required";
    if (!form.description) newErrors.description = "Description is required";
    if (!form.price) newErrors.price = "Price is required";
    if (!form.image) newErrors.image = "Image URL is required";
    if (!form.tags) newErrors.tags = "At least one tag is required";
    if (!form.inStock) newErrors.inStock = "Stock count is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    dispatch(
      createProduct({
        product: {
          ...form,
          price: Number(form.price),
          inStock: Number(form.inStock),
          tags: form.tags.split(",").map((t) => t.trim()),
        },
      }) as any
    );
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-xl max-h-[90vh] bg-popupLight dark:bg-popupDark rounded-2xl shadow-lg p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <X size={20} />
        </button>

        <h2 className="text-2xl font-bold mb-4 text-center">
          {t("Add Product")}
        </h2>

        <div className="overflow-y-auto custom-scroll max-h-[75vh] pr-2">
          <div className="space-y-4">
            <Input
              label="Product Name"
              value={form.name}
              error={errors.name}
              onChange={(value: string) => handleChange("name", value)}
            />

            <Textarea
              label="Description"
              value={form.description}
              error={errors.description}
              onChange={(value: string) => handleChange("description", value)}
            />

            <Input
              label="Price"
              type="number"
              value={form.price}
              error={errors.price}
              onChange={(value: string) => handleChange("price", value)}
            />

            <Input
              label="Image URL"
              value={form.image}
              error={errors.image}
              onChange={(value: string) => handleChange("image", value)}
            />

            <Input
              label="Tags (comma separated)"
              value={form.tags}
              error={errors.tags}
              onChange={(value: string) => handleChange("tags", value)}
            />

            <Input
              label="Stock Count"
              type="number"
              value={form.inStock}
              error={errors.inStock}
              onChange={(value: string) => handleChange("inStock", value)}
            />
          </div>

          <button
            onClick={handleSubmit}
            className="mt-6 w-full flex items-center justify-center gap-2 bg-secondaryText dark:bg-secondaryTextDark text-primaryText py-2 rounded-md hover:opacity-90 transition"
          >
            <Check size={18} />
            {t("Save Product")}
          </button>
        </div>
      </div>
    </div>
  );
};
